import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
    ClosingSetupProvider,
    OverviewStepState,
    ProjectAccountsProvider,
    isChartOfAccountsCompleteFor,
    presentClosingOverview,
} from '@modeso/aumico-lib-abcp-fe';
import { AnnualReportMetaInformationDTO, AnnualReportProvider } from '@modeso/aumico-lib-annualreport-fe';
import { CompaniesProvider } from '@modeso/aumico-lib-companies-fe';
import { AnnouncementsProvider, AumBreadcrumbComponentItem } from '@modeso/aumico-lib-core-fe';
import { IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { combineLatest, filter, of } from 'rxjs';
import { delay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { NavMandateContext, NavModule, NavStep } from '../../../../../aumico-components/src/lib';
import { TasksOverviewActions } from '../../../../../aumico-lib-cpmm-fe/src/lib/actions/tasks.actions';
import { ProjectsOverviewActions, ProjectsOverviewState } from '../../../../../aumico-lib-projects-fe/src/lib';
import { selectActiveProjectId } from '../../../../../aumico-lib-projects-fe/src/lib/reducers/projects-overview.reducer';
import { OrganizationProvider } from '../../../../../aumico-lib-users-fe/src/public-api';
import { Paths } from '../../enums/paths.enum';
import { RouteLabels } from '../../enums/routeLabels.enum';

const YEARS_SEPARATOR = '/';
const LAST_TWO_DIGITS_OF_YEAR = -2;
/** The route segment this host lives under (`:id/abcp`); the active module = the segment after it. */
const ABCP_SEGMENT = 'abcp';
/**
 * The modules whose page gets its breadcrumb from this shell. The closing workspace renders its own,
 * and the pages reused from `/cpmm` render none inside ABCP — so the crumb lives here and those pages
 * stay untouched for `/cpmm` and aumico light.
 */
const MODULES_WITH_SHELL_CRUMBS: readonly string[] = [Paths.MANDATE_MANAGEMENT, Paths.CLOSING_WORKFLOW, Paths.REPORT];

/**
 * REV-21518 — Smart host for the `:id/abcp` sibling route tree (orchestrator).
 *
 * Mirrors {@link CpmmPage} for state + routing, but renders the CONTROLLED vertical
 * `ham-navigation-bar` (REV-21353 pure view) instead of the self-routing horizontal
 * `ham-page-tab-navigation`. Because the vertical nav is controlled, THIS host owns the routing:
 * it feeds the nav its static modules, derives `activeModuleId` from the URL, navigates on
 * `moduleSelect`, and holds the ephemeral `collapsed` state. Content pages under `<router-outlet>`
 * are the SAME classes reused from `/cpmm` (no fork).
 *
 * Contract + decisions: REV-21518-context/context.md §5 (I/O), §5.3 (module mapping), §9 (D1–D5).
 *
 * CD note: default change detection on purpose. The pages under the outlet are reused from cpmm
 * (report, Mandatsverwaltung, Abschluss-Workflow) and never call `markForCheck()`; under an OnPush
 * shell their async data only rendered after the next click. ABCP's own closing pages declare
 * OnPush themselves.
 */
@UntilDestroy()
@Component({
    // Unique selector. NOTE: CpmmPage (the template) reuses `app-financial-statement-edit`, which
    // collides with the real FinancialStatementEditPage — deliberately NOT copied here.
    selector: 'app-abcp',
    // The nav sits NEXT TO the layout, not in it: inside, it scrolled with the content and the footer
    // row ran under it. The footer starts hidden — only a page that sends its buttons shows it.
    template: `<div class="abcp-shell">
        <ham-navigation-bar
            class="abcp-shell__nav"
            [mandate]="mandate"
            [modules]="modules"
            [activeModuleId]="activeModuleId"
            [activeStepId]="activeStepId"
            [expandedModuleId]="expandedModuleId"
            [collapsed]="collapsed"
            (moduleSelect)="onModuleSelect($event)"
            (stepSelect)="onStepSelect($event)"
            (collapsedToggle)="onCollapsedToggle($event)"
        ></ham-navigation-bar>
        <app-aumico-layout class="abcp-shell__layout" [hideFooter]="true">
            <ng-container content>
                <div class="abcp-shell__content">
                    <div class="abcp-shell__path" data-testid="abcp-shell-path" *ngIf="crumbItems.length > 0">
                        <aum-breadcrumb [items]="crumbItems"></aum-breadcrumb>
                    </div>
                    <router-outlet></router-outlet>
                </div>
            </ng-container>
        </app-aumico-layout>
    </div>`,
    styles: [
        `
            :host ::ng-deep .aumico-content-grid.aumico-grid-row-content {
                grid-template-rows: minmax(0, 1fr);
                min-height: 0;
            }
            :host {
                display: block;
            }
            /* Room between the breadcrumb band and the report tabs; padding so the sticky row stays white. */
            :host ::ng-deep inner-page-tab-navigation .tabs-container {
                padding-top: 16px;
            }
            /* The full window height the layout used to take on its own (100vh). */
            .abcp-shell {
                display: flex;
                align-items: stretch;
                height: 100vh;
                min-height: 0;
            }
            .abcp-shell__layout {
                display: block;
                flex: 1 1 auto;
                min-width: 0;
            }
            /* Full height is an acceptance criterion of the closing workspace: pass it down. */
            .abcp-shell__content {
                height: 100%;
                min-height: 0;
            }
            /* The same band as the closing workspace's breadcrumb row (abcp-workspace-header). */
            .abcp-shell__path {
                display: flex;
                align-items: center;
                min-height: 64px;
                padding: 16px 16px 8px;
                background: var(--white, #fff);
                border-bottom: 1px solid var(--gray-200, #e5e7eb);
            }
            .abcp-shell__path ::ng-deep .aum-breadcrumb {
                margin: 0;
                line-height: 1;
            }
            .abcp-shell__path ::ng-deep .aum-breadcrumb-parent:hover {
                color: var(--primary-700, #0a8394);
            }
            .abcp-shell__path ::ng-deep .aum-breadcrumb-active {
                font-weight: 700;
            }
        `,
    ],
})
export class AbcpPage implements OnInit, OnDestroy {
    /** The nav modules, see {@link buildModules}. Built once in ngOnInit (labels via i18next). */
    modules: NavModule[] = [];

    /** Company + fiscal year of the active mandate — fed to the nav header. Null until the report loads. */
    mandate: NavMandateContext | null = null;

    /** Active module = first path segment under `abcp`, derived from the URL (reactive on NavigationEnd). */
    activeModuleId: string | null = null;

    /** The active sub-step, derived from the URL like the active module. */
    activeStepId: string | null = null;

    /** Which module shows its steps. Follows the active module, so the open one is the one in use. */
    expandedModuleId: string | null = null;

    /** Ephemeral collapse state (D4 — not persisted). */
    collapsed = false;

    /** Firmen › Firma › Geschäftsjahr › Modul — empty on the modules that do not take it (see MODULES_WITH_SHELL_CRUMBS). */
    crumbItems: AumBreadcrumbComponentItem[] = [];

    private companyId: string | null = null;

    /** The closing's project id, once the store has resolved it — the nav's gate reads need it. */
    projectId: string | null = null;

    /**
     * REV-22479 — whether the Kontenplan is complete; `null` while unknown. Locks the two statements
     * in the nav until it is; the route guard enforces the same rule for a link or a reload. Complete
     * means every account of the default list has a position — accounts without a balance need none.
     */
    private chartComplete: boolean | null = null;

    /**
     * REV-22479 — whether the closing has been started (setup wizard). Before that the closing has no
     * steps at all: «Jahresabschluss» opens the wizard, and its sub-items would point at pages that do
     * not exist yet. `true` also when the read failed — like the route gate, it fails open.
     */
    private closingStarted = false;

    /**
     * REV-22527 — whether the accounting data of a started closing is in. While it is imported, every
     * step is locked: each one reads the accounts, and an empty page reads as a broken one.
     */
    private accountingReady = true;

    /**
     * The states of «Dokumente zuordnen» and «Bilanzkonten abstimmen» — computed by the Jahresabschluss
     * overview's own presenter (steps 3 and 4), so nav and overview cannot disagree. `null` until the
     * account list has loaded: an empty list would read as "nothing missing, nothing to reconcile".
     */
    /** The Kontenplan's marker, from the same presenter as the overview's step 1. */
    private chartState: OverviewStepState | null = null;
    private documentsState: OverviewStepState | null = null;
    private balanceSheetState: OverviewStepState | null = null;

    private companiesRequested = false;

    constructor(
        private readonly providerOfAnnualReports: AnnualReportProvider,
        private readonly organizationProvider: OrganizationProvider,
        private store: Store<ProjectsOverviewState>,
        private route: ActivatedRoute,
        private router: Router,
        private readonly actionsListener$: ActionsSubject,
        private readonly announcementsProvider: AnnouncementsProvider,
        private readonly localizedTexts: I18NextService,
        private readonly cdr: ChangeDetectorRef,
        private readonly companiesProvider: CompaniesProvider,
        private readonly accountsProvider: ProjectAccountsProvider,
        private readonly closingSetups: ClosingSetupProvider,
    ) {}

    ngOnDestroy(): void {
        this.store.dispatch(
            ProjectsOverviewActions.clearActiveProjectId(), // Clear the active project ID when leaving the page
        );
        this.store.dispatch(TasksOverviewActions.clearActiveTasks());
        this.providerOfAnnualReports.resetActiveAnnualReport(); // Reset the active annual report
    }

    ngOnInit(): void {
        this.modules = this.buildModules();
        this.activeModuleId = this.deriveActiveModuleId(this.router.url);
        this.activeStepId = this.deriveActiveStepId(this.router.url);
        this.expandedModuleId = this.activeModuleId;
        this.refreshCrumbs();

        this.announcementsProvider.dispatchGetUserAnnouncements();

        // Keep the active module in sync with the URL (the nav is controlled — it does not self-route).
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.activeModuleId = this.deriveActiveModuleId(this.router.url);
                this.activeStepId = this.deriveActiveStepId(this.router.url);
                // The module in use is the one whose steps are worth showing.
                this.expandedModuleId = this.activeModuleId;
                this.refreshCrumbs();
                this.cdr.markForCheck();
            });

        this.providerOfAnnualReports
            .getActiveAnnualReport$()
            .pipe(
                filter(
                    (activeAnnual: IAnnualReport) =>
                        (activeAnnual != null && activeAnnual.generalInformation != null) ||
                        (activeAnnual != null && activeAnnual.setupData != null),
                ),
                withLatestFrom(this.organizationProvider.getCurrentOrganization$()),
                untilDestroyed(this),
            )
            .subscribe(([annualReport]) => {
                this.mandate = this.toMandateContext(annualReport);
                this.companyId = (annualReport.generalInformation ?? annualReport.setupData).company._id ?? null;
                this.refreshCrumbs();
                this.cdr.markForCheck();
            });

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                switchMap((encryptedId: string) =>
                    this.providerOfAnnualReports.getMetaInformationByEncryptedId$(encryptedId),
                ),
                switchMap((metaInfo: AnnualReportMetaInformationDTO) =>
                    of(metaInfo).pipe(
                        filter((annualReportMetaInfo) => annualReportMetaInfo != null),
                        delay(1000), // ⏱️ wait 1 second for project meta information record to be created
                        tap(() => {
                            this.store.dispatch(
                                ProjectsOverviewActions.fetchProjectIdByAnnualReportMetaInfoId({
                                    annualReportMetaId: metaInfo._id,
                                }),
                            );
                        }),
                    ),
                ),
                untilDestroyed(this),
            )
            .subscribe({});

        this.actionsListener$
            .pipe(ofType(ProjectsOverviewActions.projectIdByAnnualReportMetaInfoIdFetchFailure), untilDestroyed(this))
            .subscribe((error) => {});

        this.store
            .select(selectActiveProjectId)
            .pipe(untilDestroyed(this))
            .subscribe((projectId) => {
                this.projectId = projectId ?? null;
                // The classification counters — one aggregation, no journal — and whether the closing is
                // started at all, which decides whether it shows steps.
                if (this.projectId !== null) {
                    this.accountsProvider.loadSummary(this.projectId);
                    this.closingSetups.load(this.projectId);
                }
                this.loadAccountsForSteps();
                this.cdr.markForCheck();
            });

        this.closingSetups
            .getGate$()
            .pipe(untilDestroyed(this))
            .subscribe((decision) => {
                const started = decision === 'started' || decision === 'unavailable';
                if (started === this.closingStarted) {
                    return;
                }
                this.closingStarted = started;
                this.loadAccountsForSteps();
                this.modules = this.buildModules();
                this.cdr.markForCheck();
            });

        this.closingSetups
            .getAccountingReady$()
            .pipe(untilDestroyed(this))
            .subscribe((ready) => {
                if (ready === this.accountingReady) {
                    return;
                }
                this.accountingReady = ready;
                this.modules = this.buildModules();
                this.cdr.markForCheck();
            });

        // The default list, not the shared one: the Kontenplan, the «Nullkonten» chip and the comparison
        // chips change the shared list, and a step's status must not follow them. An assigned document
        // patches one row of it without a reload.
        // REV-22527 — with the import's state: the presenter greys every step while it runs, for the
        // navigation exactly as for the overview.
        combineLatest([
            this.accountsProvider.stepAccountsStatus$(),
            this.accountsProvider.stepAccounts$(),
            this.closingSetups.getLaunchInFlight$(),
        ])
            .pipe(
                filter(([status]) => status === 'loaded' || status === 'idle' || status === 'failed'),
                untilDestroyed(this),
            )
            .subscribe(([status, accounts, importing]) => {
                const chartComplete = status === 'loaded' ? isChartOfAccountsCompleteFor(accounts) : null;
                const overview =
                    status === 'loaded'
                        ? presentClosingOverview({ accounts, positionLabel: (position) => position, importing })
                        : null;
                const chart = overview?.chartOfAccounts.state ?? null;
                const documents = overview?.documents.state ?? null;
                const balanceSheet = overview?.balanceSheet.state ?? null;
                if (
                    chartComplete === this.chartComplete &&
                    chart === this.chartState &&
                    documents === this.documentsState &&
                    balanceSheet === this.balanceSheetState
                ) {
                    return;
                }
                this.chartComplete = chartComplete;
                this.chartState = chart;
                this.documentsState = documents;
                this.balanceSheetState = balanceSheet;
                this.modules = this.buildModules();
                this.cdr.markForCheck();
            });
    }

    onModuleSelect(moduleId: string): void {
        this.router.navigate([moduleId], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    }

    /**
     * A sub-step was clicked: navigate to the module's segment plus the step's.
     *
     * The step id IS the child segment, so no lookup table is needed — the same choice the module
     * ids make, for the same reason.
     */
    onStepSelect(selection: { moduleId: string; stepId: string }): void {
        this.router.navigate([selection.moduleId, selection.stepId], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve',
        });
    }

    /** Ephemeral collapse toggle (D4). The output event triggers OnPush change detection here. */
    onCollapsedToggle(collapsed: boolean): void {
        this.collapsed = collapsed;
    }

    /**
     * Same path and same targets as the closing workspace's breadcrumb, so the trail reads alike in
     * every module. The fiscal year never navigates; the module is the current page.
     */
    private refreshCrumbs(): void {
        const activeModule = this.modules.find((module) => module.id === this.activeModuleId);
        if (activeModule === undefined || !MODULES_WITH_SHELL_CRUMBS.includes(activeModule.id)) {
            this.crumbItems = [];
            return;
        }

        // `companies/:companyId/…` is behind IdValidatorGuard, which checks the id against the companies
        // store and never loads it itself — a cold store would send the company crumb to not-found.
        if (!this.companiesRequested) {
            this.companiesRequested = true;
            this.companiesProvider.dispatchCompaniesList();
        }

        const items: AumBreadcrumbComponentItem[] = [
            {
                displayName: this.localizedTexts.t(RouteLabels.COMPANIES_LIST) as string,
                translated: true,
                onClick: () => this.openCompanies(),
            },
        ];
        if (this.mandate != null) {
            items.push({ displayName: this.mandate.mandantName, translated: true, onClick: () => this.openMandate() });
            items.push({ displayName: this.mandate.fiscalYearLabel, translated: true });
        }
        items.push({ displayName: activeModule.label, translated: true, isActive: true });
        this.crumbItems = items;
    }

    private openCompanies(): void {
        void this.router.navigate([this.language(), Paths.HOME, Paths.COMPANIES, Paths.OVERVIEW]);
    }

    /** The projects OF THIS COMPANY. Without the id there is no such list, so fall back to all. */
    private openMandate(): void {
        const target =
            this.companyId != null
                ? [this.language(), Paths.HOME, Paths.COMPANIES, this.companyId, Paths.PROJECT]
                : [this.language(), Paths.HOME, Paths.PROJECT, Paths.OVERVIEW];
        void this.router.navigate(target);
    }

    private language(): string {
        return this.router.url.split(/[?#;]/)[0].split('/').filter(Boolean)[0];
    }

    /**
     * The modules in working order: Mandatsverwaltung → Jahresabschluss → Bericht — id = child-route
     * segment, label resolved from RouteLabels, Lucide icon (§5.3).
     *
     * Abschluss-Workflow is not offered: ABCP users work in Jahresabschluss. Its route stays, so the
     * page is still reachable by URL.
     */
    private buildModules(): NavModule[] {
        return [
            {
                id: Paths.MANDATE_MANAGEMENT,
                label: this.localizedTexts.t(RouteLabels.REPORT_MANDATE_MANAGEMENT) as string,
                icon: 'notebook-pen',
            },
            {
                id: Paths.CLOSING,
                label: this.localizedTexts.t(RouteLabels.REPORT_CLOSING) as string,
                icon: 'list',
                steps: this.closingSteps(),
            },
            {
                id: Paths.REPORT,
                label: this.localizedTexts.t(RouteLabels.REPORT) as string,
                icon: 'chart-column',
            },
        ];
    }

    /**
     * REV-22479 — the closing's steps: the Kontenplan first, then the two statements (the accounts tab
     * differs per statement, so those two steps address exactly it).
     *
     * NONE before the closing is started: «Jahresabschluss» then opens the setup wizard, and the steps
     * would point at pages that do not exist yet. The statements stay locked until every account has a
     * position. The Kontenplan's marker says whether it is complete; the statements' stays 'none' — a
     * per-step roll-up needs state this closing does not carry yet.
     */
    /**
     * The default list behind the steps — only for a started closing, and only once: when the closing
     * pages already hold it, the store takes that over.
     */
    private loadAccountsForSteps(): void {
        if (this.closingStarted && this.projectId !== null) {
            this.accountsProvider.loadStepAccountsIfNeeded(this.projectId);
        }
    }

    private closingSteps(): NavStep[] {
        if (!this.closingStarted) {
            return [];
        }
        // Erfolgsrechnung has no rule for "done" yet — grey, like step 5 of the overview.
        const statements = [
            {
                id: Paths.CLOSING_ACCOUNTS_BS,
                labelKey: 'abcp.accountSurface.navBalanceSheet',
                status: this.balanceSheetState ?? 'none',
            },
            {
                id: Paths.CLOSING_ACCOUNTS_IS,
                labelKey: 'abcp.accountSurface.navIncomeStatement',
                status: 'none' as const,
            },
        ];

        // The import comes first: until the accounting data is in, every step waits, the Kontenplan too.
        const waiting = !this.accountingReady;
        const lockedReason = this.localizedTexts.t(
            waiting ? 'abcp.setup.import.locked' : 'abcp.chartOfAccounts.locked',
        ) as string;

        return [
            {
                id: Paths.CLOSING_CHART_OF_ACCOUNTS,
                label: this.localizedTexts.t('abcp.chartOfAccounts.title') as string,
                status: this.chartState ?? 'none',
                ...(waiting ? { locked: true, lockedReason } : {}),
            },
            {
                id: Paths.CLOSING_DOCUMENTS,
                label: this.localizedTexts.t('abcp.overview.documents.title') as string,
                status: this.documentsState ?? 'none',
                locked: waiting || this.chartComplete === false,
                lockedReason,
            },
            ...statements.map((step) => ({
                id: step.id,
                label: this.localizedTexts.t(step.labelKey) as string,
                status: step.status,
                locked: waiting || this.chartComplete === false,
                lockedReason,
            })),
        ];
    }

    /**
     * The active module id = the first path segment AFTER `abcp` in the URL. The report editor's
     * deep sub-routes (`report/general`, …) keep `report` as that first segment, so the Bericht
     * module stays active throughout the editor.
     */
    private deriveActiveModuleId(url: string): string | null {
        const segments = url.split(/[?#;]/)[0].split('/').filter(Boolean);
        const index = segments.lastIndexOf(ABCP_SEGMENT);
        if (index === -1 || index + 1 >= segments.length) {
            return null;
        }
        return segments[index + 1] || null;
    }

    /**
     * The active step = the segment AFTER the module's, when it is one of the module's steps.
     *
     * Checked against the module's own step list rather than taken blindly: the closing workspace
     * has five tabs and only two of them are steps, so `documents` must not light up a step.
     */
    private deriveActiveStepId(url: string): string | null {
        const segments = url.split(/[?#;]/)[0].split('/').filter(Boolean);
        const index = segments.lastIndexOf(ABCP_SEGMENT);
        if (index === -1 || index + 2 >= segments.length + 1) {
            return null;
        }

        const candidate = segments[index + 2] ?? null;
        if (candidate === null) {
            return null;
        }

        const activeModule = this.modules.find((module) => module.id === segments[index + 1]);

        return activeModule?.steps?.some((step) => step.id === candidate) === true ? candidate : null;
    }

    private toMandateContext(annualReport: IAnnualReport): NavMandateContext {
        if (annualReport.generalInformation != null) {
            return {
                mandantName: this.constructCompanyName(annualReport.generalInformation.company),
                fiscalYearLabel: this.constructFiscalYearLabel(
                    annualReport.generalInformation.balanceSheetDate,
                    annualReport.generalInformation.balancePreviousYearDate,
                ),
            };
        }
        return {
            mandantName: this.constructCompanyName(annualReport.setupData.company),
            fiscalYearLabel: this.constructFiscalYearLabel(annualReport.setupData.balanceSheetDate, undefined),
        };
    }

    /** "Geschäftsjahr YYYY" (single year) or "Geschäftsjahr YY/YY" (spanning two years). */
    private constructFiscalYearLabel(balanceSheetDate: string, balancePreviousYearDate: string | null): string {
        return `Geschäftsjahr ${this.getYearFromDate(balanceSheetDate, balancePreviousYearDate)}`;
    }

    private constructCompanyName(company: { name: string; label?: string }): string {
        return company.label ? `${company.name} (${company.label})` : company.name;
    }

    private getYearFromDate(balanceSheetDate: string, balancePreviousYearDate: string | null): string {
        const balanceSheetCurrentYear = new Date(balanceSheetDate).getFullYear().toString();

        if (balancePreviousYearDate == null) {
            const balanceSheetDateObj = new Date(balanceSheetDate);
            balancePreviousYearDate = new Date(
                balanceSheetDateObj.setFullYear(balanceSheetDateObj.getFullYear() - 1),
            ).toISOString();
        }
        const balanceSheetPreviousYear = new Date(balancePreviousYearDate).getFullYear().toString();

        if (balanceSheetCurrentYear === balanceSheetPreviousYear) {
            return balanceSheetCurrentYear;
        }
        const balanceSheetCurrentYearShortened = balanceSheetCurrentYear.slice(LAST_TWO_DIGITS_OF_YEAR);
        const balanceSheetPreviousYearShortened = balanceSheetPreviousYear.slice(LAST_TWO_DIGITS_OF_YEAR);

        return balanceSheetCurrentYearShortened + YEARS_SEPARATOR + balanceSheetPreviousYearShortened;
    }
}
